import React from 'react';
import { FaExclamationTriangle, FaSignInAlt } from 'react-icons/fa';
import keycloak from '../config/keycloak';

interface SessionTimeoutModalProps {
  isOpen: boolean;
  onLoginRedirect?: () => void;
}

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({ 
  isOpen, 
  onLoginRedirect 
}) => {
  if (!isOpen) return null;

  const handleLoginRedirect = () => {
    console.log('🔐 Redirecting to login due to session timeout...');
    
    if (onLoginRedirect) {
      onLoginRedirect();
    } else {
      // Default: trigger Keycloak login
      try {
        keycloak.login({
          redirectUri: window.location.origin + '/dashboard'
        });
      } catch (error) {
        console.error('Failed to redirect to login:', error);
        // Fallback: redirect to home
        window.location.href = '/';
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]">
      {/* Backdrop - prevents any interaction */}
      <div className="absolute inset-0" />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <FaExclamationTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
          Session Expired
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-6 leading-relaxed">
          Your session has timed out due to inactivity. For security reasons, 
          you have been logged out and need to sign in again to continue.
        </p>

        {/* Security notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-800">Security Notice</p>
              <p className="text-sm text-blue-700 mt-1">
                This automatic logout helps protect your data when you're away from your device.
              </p>
            </div>
          </div>
        </div>

        {/* Login button */}
        <button
          onClick={handleLoginRedirect}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2"
        >
          <FaSignInAlt className="w-4 h-4" />
          <span>Sign In Again</span>
        </button>

        {/* Footer */}
        <p className="text-xs text-gray-500 text-center mt-4">
          You will be redirected to the login page
        </p>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
